import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "../lib/api";
import { useAuth } from "../stores/auth";
import { toast } from "../stores/toast";
import { Button, Input } from "./ui";

export function SessionLockOverlay() {
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const userSecurity = useAuth((s) => s.userSecurity);
  const sessionLocked = useAuth((s) => s.sessionLocked);
  const loginWithPin = useAuth((s) => s.loginWithPin);
  const logout = useAuth((s) => s.logout);

  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  if (!sessionLocked || !user) return null;

  const canUsePin = Boolean(userSecurity?.pinEnabled && userSecurity.pinSet && user.email);

  async function submitPin(e: React.FormEvent) {
    e.preventDefault();
    if (pin.length !== 6) {
      toast("error", "Enter your 6-digit PIN");
      return;
    }
    setBusy(true);
    try {
      await loginWithPin(user!.email, pin);
      setPin("");
      toast("success", "Session unlocked");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Incorrect PIN");
      setPin("");
    } finally {
      setBusy(false);
    }
  }

  async function signInAgain() {
    await logout();
    navigate("/login");
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-brand-dark/95 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-sm rounded-2xl border border-white/10 bg-surface p-6 shadow-2xl">
        <h2 className="font-display text-lg font-bold">Session locked</h2>
        <p className="mt-1 text-xs text-white/50">
          {canUsePin ? "Enter your PIN to continue." : "Sign in again to continue."}
        </p>

        <div className="mt-4 flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-brand text-sm font-bold text-white">
            {(user.displayName ?? user.email).slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{user.displayName}</p>
            <p className="truncate text-xs text-white/50">{user.email}</p>
          </div>
        </div>

        {canUsePin ? (
          <form className="mt-5 space-y-4" onSubmit={submitPin}>
            <Input
              type="password"
              inputMode="numeric"
              maxLength={6}
              placeholder="6-digit PIN"
              autoComplete="off"
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
            />
            <div className="flex items-center justify-between gap-2">
              <button type="button" onClick={() => void signInAgain()} className="text-xs text-brand-pink">
                Sign in with password
              </button>
              <Button type="submit" disabled={busy || pin.length !== 6}>
                {busy ? "Unlocking…" : "Unlock"}
              </Button>
            </div>
          </form>
        ) : (
          <div className="mt-5 flex justify-end">
            <Button onClick={() => void signInAgain()}>Sign in</Button>
          </div>
        )}
      </div>
    </div>
  );
}
